/**
 * The hidden field shared by the public forms (contact, careers, both
 * newsletter forms, `/apply`, sign-up, forgot password and the community join
 * email step). A person never sees or reaches it: it sits
 * off-screen, out of the tab order and hidden from screen readers. A bot that
 * fills every input it finds fills this one too. The `withPublicForm`
 * (`lib/api-helpers.ts`) routes and `/api/coaching/apply` answer 200 without
 * persistence or notification side effects. The name carries no autofill
 * meaning, so no browser or password manager has a reason to fill it.
 */
export const HONEYPOT_FIELD = "ca_hp"

/** True when a parsed public-form payload contains a non-empty trap value. */
export function hasFilledHoneypot(payload: unknown): boolean {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return false
  if (!Object.prototype.hasOwnProperty.call(payload, HONEYPOT_FIELD)) return false

  const value = (payload as { [key: string]: unknown })[HONEYPOT_FIELD]
  if (value === null || value === undefined) return false
  if (typeof value === "string") return value.trim() !== ""
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === "object") return Object.keys(value).length > 0
  return true
}

/** The field's value on a submitted form, or "" when absent. Read from the DOM
 *  at submit time, because a bot sets the value without firing React events. */
export function readHoneypot(form: EventTarget | null): string {
  if (typeof HTMLFormElement === "undefined" || !(form instanceof HTMLFormElement)) return ""
  const field = form.elements.namedItem(HONEYPOT_FIELD)
  return field instanceof HTMLInputElement ? field.value : ""
}

/** The payload with the trap field attached only when something filled it, so
 *  a person's request body is byte-for-byte what it was. */
export function withHoneypot<T extends Record<string, unknown>>(payload: T, form: EventTarget | null): T {
  const trap = readHoneypot(form)
  return trap ? { ...payload, [HONEYPOT_FIELD]: trap } : payload
}
